import { computed, ref } from 'vue'
import axios from 'axios'
import { DocumentsApi } from '../services/documentsApi'
import type { DocumentItem, FetchDocumentsParams } from '../types/document.types'
import { type TableSortItem, mapColumnSortToApi } from '../utils/documentSort'
import {
  getBlobErrorMessage,
  parseContentDispositionFilename,
  saveBlobAsFile,
} from '../utils/fileHelpers'
import { highlightSearchParts } from '@/utils/highlightSearchParts'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'

export function useDocuments() {
  const documents = ref<DocumentItem[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const page = ref(1)
  const { itemsPerPage } = useTableItemsPerPage('documents', 25)
  const totalItems = ref(0)
  const sortBy = ref<TableSortItem[]>([])

  const filters = ref<FetchDocumentsParams>({})

  let requestSequence = 0

  const loadDocuments = async () => {
    const requestId = ++requestSequence
    isLoading.value = true
    error.value = null
    try {
      const params: FetchDocumentsParams = {
        ...filters.value,
        page: page.value,
        page_size: itemsPerPage.value,
      }
      const response = await DocumentsApi.fetchDocuments(params)
      if (requestId !== requestSequence) return
      // Use the accepted response's query, not the input being edited while it loads.
      const numberQuery = [params.search, params.numbers?.replace(/,/g, ' ')].filter(Boolean).join(' ')
      documents.value = response.results.map(item => ({
        ...item,
        search_number_parts: highlightSearchParts(item.number, numberQuery)
          .map(({ text, hit }) => ({ text, highlight: hit })),
      }))
      totalItems.value = response.count ?? 0
    } catch (err) {
      if (requestId !== requestSequence) return
      error.value = err instanceof Error ? err.message : 'Ошибка загрузки документов'
      console.error('[loadDocuments] Ошибка:', err)
    } finally {
      if (requestId === requestSequence) isLoading.value = false
    }
  }

  const updatePagination = (opts: {
    page?: number
    itemsPerPage?: number
    sortBy?: TableSortItem[]
  }) => {
    const newSortBy = opts.sortBy ?? sortBy.value
    const newApiSort = mapColumnSortToApi(newSortBy[0])
    const sortChanged = newApiSort !== filters.value.sort

    if (opts.sortBy !== undefined) {
      sortBy.value = opts.sortBy
    }

    if (sortChanged) {
      filters.value = { ...filters.value, sort: newApiSort }
      page.value = 1
      if (opts.itemsPerPage !== undefined) {
        itemsPerPage.value = opts.itemsPerPage
      }
      loadDocuments()

      return
    }

    const newPage = opts.page ?? page.value
    const newItemsPerPage = opts.itemsPerPage ?? itemsPerPage.value

    if (
      !sortChanged
      && newPage === page.value
      && newItemsPerPage === itemsPerPage.value
    ) {
      return
    }

    page.value = newPage
    itemsPerPage.value = newItemsPerPage
    loadDocuments()
  }

  const getSortIcon = (columnKey: string) => {
    const current = sortBy.value[0]
    if (!current || current.key !== columnKey) {
      return 'ri-arrow-up-down-line'
    }

    return current.order === 'asc' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'
  }

  const toggleColumnSort = (columnKey: string) => {
    const current = sortBy.value[0]
    let nextSort: TableSortItem[]

    if (!current || current.key !== columnKey) {
      nextSort = [{ key: columnKey, order: 'asc' }]
    } else if (current.order === 'asc') {
      nextSort = [{ key: columnKey, order: 'desc' }]
    } else {
      nextSort = []
    }

    updatePagination({ page: 1, sortBy: nextSort })
  }

  const updateFilters = async (newFilters: Partial<FetchDocumentsParams>) => {
    if ('sort' in newFilters && newFilters.sort === undefined) {
      sortBy.value = []
    }
    filters.value = { ...filters.value, ...newFilters }
    page.value = 1
    await loadDocuments()
  }

  const reloadDocuments = async () => {
    await loadDocuments()
  }

  const resetFilters = async () => {
    filters.value = {}
    sortBy.value = []
    page.value = 1
    await loadDocuments()
  }

  const deleteDocuments = async (documentIds: string | string[]) => {
    try {
      await DocumentsApi.deleteDocuments(documentIds)
      await loadDocuments()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Ошибка удаления документов'
      throw err
    }
  }

  /** Скачивает документы и папки одним запросом (файл или zip-архив) */
  const downloadDocuments = async (documentIds: string[], folderIds: string[] = []) => {
    try {
      const response = await DocumentsApi.downloadDocuments(documentIds, folderIds)
      const blob = response.data
      const blobError = await getBlobErrorMessage(blob)

      if (blobError) {
        throw new Error(blobError)
      }

      const totalCount = documentIds.length + folderIds.length
      const filename = parseContentDispositionFilename(response.headers['content-disposition'])
        ?? (totalCount === 1
          ? documents.value.find(d => d.id === (documentIds[0] || folderIds[0]))?.name || 'download'
          : `documents_${totalCount}.zip`)

      saveBlobAsFile(blob, filename)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data instanceof Blob) {
        const message = await getBlobErrorMessage(err.response.data)
        if (message) {
          error.value = message
          throw new Error(message)
        }
      }

      error.value = err instanceof Error ? err.message : 'Ошибка скачивания'
      throw err
    }
  }

  const moveDocuments = async (documentIds: string[], folderIds: string[], targetFolderId: string) => {
    try {
      await DocumentsApi.moveDocuments(documentIds, folderIds, targetFolderId)
      await loadDocuments()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Ошибка переноса'
      throw err
    }
  }

  const isEmpty = computed(() => !isLoading.value && documents.value.length === 0)

  return {
    documents,
    isLoading,
    error,
    isEmpty,
    page,
    itemsPerPage,
    totalItems,
    sortBy,
    getSortIcon,
    toggleColumnSort,
    updateFilters,
    reloadDocuments,
    updatePagination,
    resetFilters,
    deleteDocuments,
    downloadDocuments,
    moveDocuments,
  }
}
